import {useState} from 'react'
import {useMutation, useQueryClient} from '@tanstack/react-query'
import {Play} from 'lucide-react'
import {AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger} from '@/components/ui/alert-dialog'
import {Button} from '@/components/ui/button'
import {Icon} from '@/components/shared/Icon'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {useAuth} from '@/contexts/auth-context'
import {permissions} from '@/lib/permissions'
import {useToast} from '@/hooks/use-toast'

export function TriggerFullEvaluationButton() {
    const i18n = useI18n()
    const services = useServices()
    const {hasPermission} = useAuth()
    const {toast} = useToast()
    const queryClient = useQueryClient()
    const [open, setOpen] = useState(false)

    const canTrigger = hasPermission(permissions.jobs.trigger)

    const mutation = useMutation({
        mutationFn: () => services.autoAssignRules.triggerFullEvaluation(),
        onSuccess: () => {
            toast({description: i18n.autoAssignRules.triggerFullEvaluation.successToast})
            queryClient.invalidateQueries({queryKey: ['auto-assign-executions']})
            setOpen(false)
        },
        onError: () => {
            toast({description: i18n.autoAssignRules.triggerFullEvaluation.errorToast, variant: 'destructive'})
        },
    })

    if (!canTrigger) {
        return null
    }

    return (
        <AlertDialog open={open} onOpenChange={setOpen}>
            <AlertDialogTrigger asChild>
                <Button
                    variant="outline"
                    data-testid="btn-trigger-full-evaluation"
                >
                    <Icon icon={Play} size="md" className="mr-2"/>
                    {i18n.autoAssignRules.triggerFullEvaluation.buttonLabel}
                </Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="trigger-full-evaluation-dialog">
                <AlertDialogHeader>
                    <AlertDialogTitle>{i18n.autoAssignRules.triggerFullEvaluation.confirmTitle}</AlertDialogTitle>
                    <AlertDialogDescription>{i18n.autoAssignRules.triggerFullEvaluation.confirmBody}</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel data-testid="btn-trigger-full-evaluation-cancel">{i18n.common.cancel}</AlertDialogCancel>
                    <AlertDialogAction
                        data-testid="btn-trigger-full-evaluation-confirm"
                        onClick={(e) => {
                            e.preventDefault()
                            mutation.mutate()
                        }}
                        disabled={mutation.isPending}
                    >
                        {mutation.isPending ? i18n.autoAssignRules.triggerFullEvaluation.running : i18n.autoAssignRules.triggerFullEvaluation.confirmButton}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    )
}
